import React, { useState, useEffect } from 'react';
import {
  Building2,
  Truck,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Eye,
  EyeOff,
  FileText,
  User,
  Phone,
  PhoneCall,
  Mail,
  MapPin,
  Calendar,
  X,
  AlertCircle,
  ExternalLink,
  CreditCard,
  Shield,
  Activity,
  Check,
  Bed,
  Compass,
  QrCode,
  ListOrdered,
  DollarSign,
  Maximize2,
  Minimize2,
  RefreshCw,
  Edit3,
  Key,
  Lock,
  Sparkles,
  ArrowRight,
  Copy
} from 'lucide-react';
import { v1ApiClient } from '../../services/api.js';

export const DISTANCE_SLABS = [
  { key: 'slab_0_10', label: '0–10 km' },
  { key: 'slab_10_15', label: '10.1–15 km' },
  { key: 'slab_15_20', label: '15.1–20 km' },
  { key: 'slab_20_25', label: '20.1–25 km' },
  { key: 'slab_25_30', label: '25.1–30 km' },
  { key: 'slab_30_35', label: '30.1–35 km' },
  { key: 'slab_35_40', label: '35.1–40 km' },
  { key: 'slab_40_45', label: '40.1–45 km' },
  { key: 'slab_45_50', label: '45.1–50 km' },
  { key: 'slab_50_55', label: '50.1–55 km' },
  { key: 'slab_55_60', label: '55.1–60 km' },
  { key: 'slab_60_65', label: '60.1–65 km' },
  { key: 'slab_65_70', label: '65.1–70 km' },
  { key: 'slab_70_75', label: '70.1–75 km' },
  { key: 'slab_75_80', label: '75.1–80 km' },
  { key: 'slab_80_85', label: '80.1–85 km' },
  { key: 'slab_85_90', label: '85.1–90 km' },
  { key: 'slab_90_95', label: '90.1–95 km' },
  { key: 'slab_95_100', label: '95.1–100 km' }
];

// Helper to render standard value or '-'
const renderVal = (v) => {
  if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
    return '-';
  }
  if (Array.isArray(v)) {
    return v.join(', ');
  }
  return v;
};

// Safe helper to guarantee arrays even from stringified JSON or null
const ensureArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
    } catch (_) {}
  }
  return [];
};

// Helper to render document status: if present, show received badge + view link; else show '-'
const renderDocBadge = (label, docVal) => {
  const hasDoc = Boolean(docVal && docVal !== '' && docVal !== '—' && docVal !== '-');
  if (!hasDoc) {
    return (
      <div className="flex items-center justify-between py-2 px-3.5 rounded-xl bg-slate-50/80 border border-slate-200/80 text-xs">
        <span className="font-medium text-slate-600 truncate">{label}</span>
        <span className="font-semibold text-slate-400 pl-2 text-xs">-</span>
      </div>
    );
  }

  const isLink = typeof docVal === 'string' && (docVal.startsWith('http://') || docVal.startsWith('https://') || docVal.startsWith('blob:') || docVal.startsWith('/'));
  const fileName = typeof docVal === 'string' ? (docVal.split('/').pop() || docVal) : 'Document Attached';

  return (
    <div className="flex items-center justify-between py-2 px-3.5 rounded-xl bg-emerald-50/70 border border-emerald-200/80 text-xs">
      <div className="flex items-center gap-2 truncate pr-2">
        <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
          <Check className="w-3 h-3 stroke-[2.5]" />
        </div>
        <div className="truncate">
          <span className="font-semibold text-slate-800 block truncate">{label}</span>
          <span className="text-[10px] text-emerald-800 font-mono truncate block">
            {fileName}
          </span>
        </div>
      </div>
      {isLink ? (
        <a
          href={docVal}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 px-2 py-0.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-[10px] flex items-center gap-1 transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          <span>View</span>
        </a>
      ) : (
        <span className="shrink-0 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 text-[10px] font-semibold">
          Received
        </span>
      )}
    </div>
  );
};

// Helper to extract initials
const getInitials = (name) => {
  if (!name) return 'CP';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

export const OnboardingApplicationDetailModal = ({
  selectedApp,
  onClose,
  onApprove,
  actionLoading,
  decisionAction,
  setDecisionAction,
  reviewNotes,
  setReviewNotes,
  decisionError,
  setDecisionError,
  handleDecisionSubmit,
  rejectionPresets = [],
  changeRequestPresets = []
}) => {
  if (!selectedApp) return null;

  const appData = typeof selectedApp?.data === 'string'
    ? (() => {
        try { return JSON.parse(selectedApp.data); } catch (_) { return {}; }
      })()
    : (selectedApp?.data || {});

  selectedApp = { ...selectedApp, data: appData };

  const isFleetApp = selectedApp.partner_type === 'FLEET' || appData?.partnerType === 'fleet';
  const [activeTab, setActiveTab] = useState('step1');
  const [adminRateCityIdx, setAdminRateCityIdx] = useState(0);
  const [mapViewMode, setMapViewMode] = useState('roadmap');
  const [isMapExpanded, setIsMapExpanded] = useState(false);

  // Phone Call Verification & Admin Activation Form State
  const [fleetOperators, setFleetOperators] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [passwordCopied, setPasswordCopied] = useState(false);

  const currentAppId = selectedApp?.application_id || selectedApp?.id;

  const [activationForm, setActivationForm] = useState({
    name: selectedApp.name || selectedApp.data?.hospital_name || selectedApp.data?.operator_name || '',
    license_number: selectedApp.license_number || selectedApp.data?.license_number || selectedApp.data?.business_license || '',
    address: selectedApp.data?.full_address || selectedApp.data?.address || selectedApp.address || '',
    city: selectedApp.city || selectedApp.data?.city || '',
    state: selectedApp.state || selectedApp.data?.state || '',
    pincode: selectedApp.pincode || selectedApp.data?.pincode || selectedApp.data?.primary_pincode || '',
    operator_id: selectedApp.operator_id || selectedApp.data?.operator_id || '',
    admin_name: selectedApp.admin_name || selectedApp.data?.admin_name || '',
    admin_email: selectedApp.admin_email || selectedApp.data?.admin_email || selectedApp.data?.email || '',
    admin_phone: selectedApp.admin_phone || selectedApp.data?.admin_phone || selectedApp.data?.mobile || '',
    admin_password: '',
    confirm_password: '',
    contact_info: selectedApp.data?.contact_info || selectedApp.data?.admin_phone || selectedApp.admin_phone || '',
    emergency_contact: selectedApp.data?.emergency_contact || '',
    service_area: selectedApp.data?.service_area || ''
  });

  useEffect(() => {
    if (!selectedApp) return;
    setActivationForm({
      name: selectedApp.name || selectedApp.data?.hospital_name || selectedApp.data?.operator_name || '',
      license_number: selectedApp.license_number || selectedApp.data?.license_number || selectedApp.data?.business_license || '',
      address: selectedApp.data?.full_address || selectedApp.data?.address || selectedApp.address || '',
      city: selectedApp.city || selectedApp.data?.city || '',
      state: selectedApp.state || selectedApp.data?.state || '',
      pincode: selectedApp.pincode || selectedApp.data?.pincode || selectedApp.data?.primary_pincode || '',
      operator_id: selectedApp.operator_id || selectedApp.data?.operator_id || '',
      admin_name: selectedApp.admin_name || selectedApp.data?.admin_name || '',
      admin_email: selectedApp.admin_email || selectedApp.data?.admin_email || selectedApp.data?.email || '',
      admin_phone: selectedApp.admin_phone || selectedApp.data?.admin_phone || selectedApp.data?.mobile || '',
      admin_password: '',
      confirm_password: '',
      contact_info: selectedApp.data?.contact_info || selectedApp.data?.admin_phone || selectedApp.admin_phone || '',
      emergency_contact: selectedApp.data?.emergency_contact || '',
      service_area: selectedApp.data?.service_area || ''
    });
    setFormErrors({});
  }, [currentAppId]);

  useEffect(() => {
    const fetchOps = async () => {
      try {
        const res = await v1ApiClient.get('/onboarding/lookups/fleet-operators');
        if (res.data?.data) {
          setFleetOperators(res.data.data);
        } else if (Array.isArray(res.data)) {
          setFleetOperators(res.data);
        }
      } catch (e) {
        console.warn('Could not load fleet operators lookup', e);
      }
    };
    fetchOps();
  }, []);

  const handleGeneratePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let pwd = 'Risa@';
    for (let i = 0; i < 4; i++) {
      pwd += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    pwd += String(Math.floor(10 + Math.random() * 90));
    setActivationForm((prev) => ({
      ...prev,
      admin_password: pwd,
      confirm_password: pwd
    }));
    setFormErrors((prev) => ({ ...prev, admin_password: '', confirm_password: '' }));
    setPasswordCopied(true);
    setTimeout(() => setPasswordCopied(false), 3000);
  };

  const handleSaveAndApprove = () => {
    const errors = {};
    if (!activationForm.name?.trim()) {
      errors.name = isFleetApp ? 'Fleet Operator Name is required' : 'Hospital Name is required';
    }
    if (isFleetApp) {
      if (!activationForm.contact_info?.trim()) {
        errors.contact_info = 'Contact information is required';
      }
    } else {
      if (!activationForm.license_number?.trim()) {
        errors.license_number = 'Hospital license number is required';
      }
      if (!activationForm.address?.trim()) {
        errors.address = 'Hospital address is required';
      }
      if (!activationForm.city?.trim()) {
        errors.city = 'City is required';
      }
      if (!activationForm.state?.trim()) {
        errors.state = 'State is required';
      }
      if (!activationForm.pincode?.trim()) {
        errors.pincode = 'Pincode is required';
      }
    }

    if (!activationForm.admin_name?.trim()) {
      errors.admin_name = 'Admin name is required';
    }
    if (!activationForm.admin_email?.trim()) {
      errors.admin_email = 'Admin email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(activationForm.admin_email.trim())) {
      errors.admin_email = 'Valid email address is required';
    }
    if (!activationForm.admin_phone?.trim()) {
      errors.admin_phone = 'Admin phone number is required';
    } else if (activationForm.admin_phone.replace(/\D/g, '').length < 10) {
      errors.admin_phone = 'Admin phone must be at least 10 digits';
    }

    if (!activationForm.admin_password || activationForm.admin_password.length < 6) {
      errors.admin_password = 'Temporary password is required (minimum 6 characters)';
    }

    if (isFleetApp && activationForm.admin_password !== activationForm.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setActiveTab(isFleetApp ? 'step7' : 'step6');
      return;
    }

    setFormErrors({});
    if (onApprove) {
      onApprove(activationForm);
    }
  };

  const modalTabs = isFleetApp
    ? [
        { id: 'step1', num: 1, label: 'Step 1: Fleet Owner Profile' },
        { id: 'step2', num: 2, label: 'Step 2: Capacity & Coverage' },
        { id: 'step3', num: 3, label: 'Step 3: Rides & Clients' },
        { id: 'step4', num: 4, label: 'Step 4: Rates' },
        { id: 'step5', num: 5, label: 'Step 5: Add Ambulances' },
        { id: 'step6', num: 6, label: 'Step 6: Review & Submission' },
        { id: 'step7', num: 7, label: 'Step 7: Activation Form' }
      ]
    : [
        { id: 'step1', num: 1, label: 'Step 1: Hospital Profile' },
        { id: 'step2', num: 2, label: 'Step 2: Facilities & Contacts' },
        { id: 'step3', num: 3, label: 'Step 3: Ambulance Requirements' },
        { id: 'step4', num: 4, label: 'Step 4: Areas & Destinations' },
        { id: 'step5', num: 5, label: 'Step 5: Review & Submission' },
        { id: 'step6', num: 6, label: 'Step 6: Activation Form' }
      ];

  const renderMapBlock = () => {
    const rawAddress = [
      selectedApp.data?.full_address || selectedApp.data?.address || selectedApp.address,
      selectedApp.city,
      selectedApp.state,
      selectedApp.pincode || selectedApp.data?.primary_pincode
    ].filter(Boolean).join(', ');

    const locationQuery = selectedApp.data?.google_coordinates?.trim() || rawAddress;

    if (!locationQuery) {
      return (
        <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-2 shadow-2xs font-sans">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center">
              <MapPin className="w-4 h-4" />
            </div>
            <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
              REGISTERED ADDRESS & OPERATIONAL ZONE
            </span>
          </div>
          <p className="text-slate-500 text-xs italic pl-9">
            Address and GPS coordinates not provided by applicant.
          </p>
        </div>
      );
    }

    const googleMapsEmbedUrl = `https://maps.google.com/maps?q=${encodeURIComponent(locationQuery)}&t=${mapViewMode === 'satellite' ? 'k' : 'm'}&z=16&ie=UTF8&iwloc=&output=embed`;
    const directMapsUrl = selectedApp.data?.google_maps_link || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(locationQuery)}`;

    return (
      <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3 shadow-2xs font-sans">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="space-y-1.5 flex-1">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <MapPin className="w-4 h-4" />
              </div>
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                REGISTERED ADDRESS & OPERATIONAL ZONE
              </span>
            </div>
            <p className="font-semibold text-slate-800 text-sm pl-9">
              {renderVal(selectedApp.data?.full_address || selectedApp.data?.address || selectedApp.address)}
            </p>
            <p className="text-slate-500 text-xs font-normal pl-9">
              {renderVal(selectedApp.city)}, {renderVal(selectedApp.state)} - {renderVal(selectedApp.pincode || selectedApp.data?.primary_pincode)}
            </p>
            {selectedApp.data?.google_coordinates && (
              <p className="text-slate-600 text-xs font-mono pl-9 flex items-center gap-1.5 pt-0.5">
                <Compass className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <span>GPS Coordinates: <strong className="text-slate-800 font-semibold">{selectedApp.data.google_coordinates}</strong></span>
              </p>
            )}
            <div className="pl-9 pt-1 flex items-center gap-3 flex-wrap">
              <a
                href={directMapsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold text-xs"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>Open in Google Maps →</span>
              </a>
              <button
                type="button"
                onClick={() => setIsMapExpanded(prev => !prev)}
                className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-800 font-semibold text-xs cursor-pointer"
              >
                {isMapExpanded ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
                <span>{isMapExpanded ? 'Collapse Map' : 'Enlarge Map'}</span>
              </button>
            </div>
          </div>

          {!isMapExpanded && (
            <div className="w-full sm:w-72 md:w-80 h-44 rounded-xl border border-slate-200 overflow-hidden relative shadow-xs shrink-0 bg-slate-100 flex flex-col">
              <div className="absolute top-2 left-2 right-2 z-10 flex items-center justify-between pointer-events-none">
                <span className="px-2 py-0.5 rounded-md bg-white/95 backdrop-blur-xs text-[10px] font-semibold text-slate-700 shadow-xs border border-slate-200/80 pointer-events-auto flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Live Geo View
                </span>
                <div className="flex items-center gap-1 bg-white/95 backdrop-blur-xs p-0.5 rounded-lg border border-slate-200/80 shadow-xs pointer-events-auto">
                  <button
                    type="button"
                    onClick={() => setMapViewMode('roadmap')}
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all cursor-pointer ${
                      mapViewMode === 'roadmap' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    Map
                  </button>
                  <button
                    type="button"
                    onClick={() => setMapViewMode('satellite')}
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all cursor-pointer ${
                      mapViewMode === 'satellite' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    Satellite
                  </button>
                </div>
              </div>

              <iframe
                title="Live Location Map"
                src={googleMapsEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full rounded-xl"
              />
            </div>
          )}
        </div>

        {isMapExpanded && (
          <div className="w-full h-72 sm:h-80 rounded-2xl border border-slate-300 overflow-hidden relative shadow-sm bg-slate-100 flex flex-col mt-2">
            <div className="absolute top-3 left-3 right-3 z-10 flex items-center justify-between pointer-events-none">
              <span className="px-2.5 py-1 rounded-lg bg-white/95 backdrop-blur-xs text-xs font-semibold text-slate-800 shadow-xs border border-slate-200 pointer-events-auto flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>{locationQuery}</span>
              </span>
              <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur-xs p-1 rounded-xl border border-slate-200 shadow-xs pointer-events-auto">
                <button
                  type="button"
                  onClick={() => setMapViewMode('roadmap')}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                    mapViewMode === 'roadmap' ? 'bg-blue-600 text-white shadow-2xs' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  Street Map
                </button>
                <button
                  type="button"
                  onClick={() => setMapViewMode('satellite')}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                    mapViewMode === 'satellite' ? 'bg-blue-600 text-white shadow-2xs' : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  Satellite Photography
                </button>
                <button
                  type="button"
                  onClick={() => setIsMapExpanded(false)}
                  className="p-1 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 cursor-pointer ml-1"
                  title="Minimize map"
                >
                  <Minimize2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <iframe
              title="Full Live Location Map"
              src={googleMapsEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full h-full"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-3 sm:p-5 overflow-y-auto font-sans"
      style={{ zIndex: 9999 }}
    >
      <div className="bg-white rounded-3xl max-w-4xl w-full p-6 sm:p-8 shadow-2xl border border-slate-200 relative max-h-[92vh] flex flex-col animate-fade-in font-sans antialiased text-slate-700">
        {/* Modal Header */}
        <div className="flex items-start justify-between border-b border-slate-100 pb-4 mb-4 shrink-0">
          <div className="flex items-center gap-3.5">
            <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 font-semibold shrink-0">
              {selectedApp.partner_type === 'HOSPITAL' ? <Building2 className="w-6 h-6" /> : <Truck className="w-6 h-6" />}
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-xl sm:text-2xl font-semibold text-slate-800 tracking-tight">
                  {selectedApp.name}
                </h2>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-semibold ${
                    selectedApp.status === 'APPROVED'
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : selectedApp.status === 'SUBMITTED'
                      ? 'bg-amber-50 text-amber-700 border border-amber-200'
                      : selectedApp.status === 'CHANGES_REQUESTED'
                      ? 'bg-purple-50 text-purple-700 border border-purple-200'
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  <span>{selectedApp.status}</span>
                </span>
              </div>
              <p className="text-xs text-slate-500 font-normal mt-1">
                Application Code: <span className="font-semibold text-blue-600 font-mono">{selectedApp.application_code || 'DRAFT'}</span>
                {' '}| Type: <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-medium text-[11px]">{selectedApp.partner_type}</span>
                {selectedApp.submitted_at && ` | Submitted: ${new Date(selectedApp.submitted_at).toLocaleString()}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Previous Review Notes Banner (if any) */}
        {selectedApp.review_notes && (
          <div className="mb-4 p-3.5 rounded-2xl bg-purple-50 border border-purple-200 text-xs text-purple-900 flex items-start gap-2.5 shrink-0">
            <AlertTriangle className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold block">Previous Review Notes:</span>
              <span>{selectedApp.review_notes}</span>
            </div>
          </div>
        )}

        {/* Numbered Step Navigation Tabs */}
        <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-5 shrink-0 overflow-x-auto text-xs">
          {modalTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3.5 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 font-medium text-xs ${
                  isActive
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'bg-white text-slate-600 border border-slate-200/80 hover:bg-slate-50'
                }`}
              >
                <span
                  className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                    isActive ? 'bg-white text-blue-600' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  {tab.num}
                </span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Scrollable Content Body with Clean Step-Wise Organization */}
        <div className="overflow-y-auto flex-1 pr-1 space-y-4 text-xs text-slate-700">
          {isFleetApp ? (
            <>
              {/* FLEET STEP 1: FLEET OWNER PROFILE */}
              {activeTab === 'step1' && (
                <div className="space-y-4">
                  {/* 4-Grid Stats Row */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
                    <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div className="truncate">
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
                          BUSINESS LICENSE
                        </span>
                        <p className="font-semibold text-slate-800 text-xs sm:text-sm mt-0.5 truncate">
                          {renderVal(selectedApp.license_number || selectedApp.data?.business_license)}
                        </p>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div className="truncate">
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
                          CITY, STATE
                        </span>
                        <p className="font-semibold text-slate-700 text-xs sm:text-sm mt-0.5 truncate">
                          {renderVal(selectedApp.city)}, {renderVal(selectedApp.state)}
                        </p>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <QrCode className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
                          PINCODE
                        </span>
                        <p className="font-semibold text-slate-700 text-xs sm:text-sm mt-0.5">
                          {renderVal(selectedApp.pincode || selectedApp.data?.primary_pincode)}
                        </p>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <Truck className="w-5 h-5" />
                      </div>
                      <div className="truncate">
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
                          SERVICE AREA
                        </span>
                        <p className="font-semibold text-blue-600 text-xs sm:text-sm mt-0.5 truncate">
                          {renderVal(selectedApp.data?.service_area)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Interactive Base Hub Map Card */}
                  {renderMapBlock()}

                  {/* Admin Account & Emergency Contact Card */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                          <User className="w-4 h-4" />
                        </div>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                          PRIMARY ADMIN ACCOUNT
                        </span>
                      </div>
                      <div className="pl-9 space-y-1">
                        <p className="font-semibold text-slate-800 text-sm">{renderVal(selectedApp.admin_name || selectedApp.data?.admin_name)}</p>
                        <p className="text-slate-500 text-xs flex items-center gap-1.5 font-normal">
                          <Mail className="w-3.5 h-3.5 text-slate-400" />
                          <span>{renderVal(selectedApp.admin_email || selectedApp.data?.admin_email || selectedApp.data?.email)}</span>
                        </p>
                        <p className="text-slate-500 text-xs flex items-center gap-1.5 font-normal">
                          <Phone className="w-3.5 h-3.5 text-slate-400" />
                          <span>{selectedApp.admin_phone_country_code || '+91'} {renderVal(selectedApp.admin_phone || selectedApp.data?.admin_phone || selectedApp.data?.mobile)}</span>
                        </p>
                        {selectedApp.data?.whatsapp_number && (
                          <p className="text-slate-500 text-xs flex items-center gap-1.5 font-normal">
                            <span className="w-3.5 h-3.5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-[9px] font-bold">W</span>
                            <span>WhatsApp: <strong className="text-slate-700 font-semibold">{selectedApp.data.whatsapp_number}</strong></span>
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                          <Phone className="w-4 h-4" />
                        </div>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                          DIRECT EMERGENCY / OPS CONTACT
                        </span>
                      </div>
                      <div className="pl-9 space-y-1">
                        <p className="text-slate-700 text-sm font-normal">
                          Emergency Phone: <strong className="text-slate-800 font-semibold">{renderVal(selectedApp.data?.emergency_contact || selectedApp.admin_phone)}</strong>
                        </p>
                        {selectedApp.data?.contact_info && selectedApp.data.contact_info !== selectedApp.admin_phone && (
                          <p className="text-slate-500 text-xs pt-0.5">
                            Alternate Contact: <span className="font-semibold text-slate-700">{selectedApp.data.contact_info}</span>
                          </p>
                        )}
                        {selectedApp.data?.service_area && (
                          <p className="text-slate-500 text-xs pt-0.5">
                            Territory: <span className="font-semibold text-slate-700">{selectedApp.data.service_area}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Business & Tax Details */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                    <span className="text-xs font-semibold text-slate-800 block">Business & Tax Details:</span>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">WhatsApp Number</span>
                        <p className="font-semibold text-slate-800 text-xs mt-0.5">
                          {renderVal(selectedApp.data?.whatsapp_number)}
                          {selectedApp.data?.is_whatsapp_same && (
                            <span className="text-[10px] text-blue-600 block font-normal">(Same as mobile)</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">GSTIN</span>
                        <p className="font-semibold text-slate-800 font-mono text-xs mt-0.5">{renderVal(selectedApp.data?.gstin)}</p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">PAN</span>
                        <p className="font-semibold text-slate-800 font-mono text-xs mt-0.5">{renderVal(selectedApp.data?.pan)}</p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Years in Business</span>
                        <p className="font-semibold text-slate-800 text-xs mt-0.5">
                          {selectedApp.data?.years_in_business ? `${selectedApp.data.years_in_business} Years` : (selectedApp.data?.years_in_ambulance_business ? `${selectedApp.data.years_in_ambulance_business} Years` : '-')}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Registered Address</span>
                        <p className="font-semibold text-slate-800 text-xs mt-0.5 truncate">{renderVal(selectedApp.data?.registered_address)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Step 1 Document Check */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-2.5">
                    <span className="text-xs font-semibold text-slate-800 block">Step 1 Verification Document:</span>
                    {renderDocBadge(
                      'Commercial Business License / Registration Certificate',
                      selectedApp.data?.business_license_document || selectedApp.data?.documents?.business_license || selectedApp.data?.documents?.license_document
                    )}
                  </div>
                </div>
              )}

              {/* FLEET STEP 2: CAPACITY & COVERAGE */}
              {activeTab === 'step2' && (
                <div className="space-y-4">
                  {(() => {
                    const baseHub =
                      selectedApp.data?.primary_base_location ||
                      selectedApp.data?.ambulances?.[0]?.pickup_stands?.[0]?.stand_name ||
                      selectedApp.data?.ambulances?.[0]?.pickup_stands?.[0]?.map_location ||
                      selectedApp.data?.ambulances?.[0]?.parking_location ||
                      (selectedApp.data?.city && selectedApp.data?.state ? `${selectedApp.data.city}, ${selectedApp.data.state}` : selectedApp.data?.city || selectedApp.data?.registered_address);

                    const radiusVal =
                      selectedApp.data?.service_radius_km ||
                      selectedApp.data?.ambulances?.[0]?.pickup_stands?.[0]?.service_radius_km ||
                      selectedApp.data?.ambulances?.[0]?.service_radius;

                    const operatingHoursVal =
                      selectedApp.data?.operating_hours ||
                      '24x7';

                    const intercityVal =
                      selectedApp.data?.intercity_available ||
                      selectedApp.data?.ambulances?.[0]?.pickup_stands?.[0]?.intercity_available ||
                      'Available';

                    return (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
                        <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">PRIMARY BASE HUB</span>
                          <p className="font-semibold text-slate-800 text-xs sm:text-sm mt-0.5 line-clamp-1" title={baseHub}>
                            {renderVal(baseHub)}
                          </p>
                        </div>
                        <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">SERVICE RADIUS</span>
                          <p className="font-semibold text-slate-800 text-xs sm:text-sm mt-0.5">
                            {radiusVal ? `${radiusVal} km` : 'City Jurisdiction'}
                          </p>
                        </div>
                        <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">OPERATING HOURS</span>
                          <p className="font-semibold text-emerald-700 text-xs sm:text-sm mt-0.5">
                            {renderVal(operatingHoursVal)}
                          </p>
                        </div>
                        <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50">
                          <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">INTERCITY TRIPS</span>
                          <p className="font-semibold text-slate-800 text-xs sm:text-sm mt-0.5">
                            {renderVal(intercityVal)}
                          </p>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Fleet Capacities Table (Attached column hidden) */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-800 block">Ambulance Fleet Capacity Breakdown:</span>
                      {Array.isArray(selectedApp.data?.fleet_capacities) && selectedApp.data.fleet_capacities.length > 0 && (
                        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded border border-blue-100">
                          Total Vehicles: {selectedApp.data.fleet_capacities.reduce((acc, c) => acc + (Number(c.owned) || 0) + (Number(c.attached) || 0), 0)}
                        </span>
                      )}
                    </div>
                    {Array.isArray(selectedApp.data?.fleet_capacities) && selectedApp.data.fleet_capacities.length > 0 && selectedApp.data.fleet_capacities.some(fc => fc.ambulance_type || fc.owned) ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-slate-200 text-slate-500 font-semibold uppercase text-[10px]">
                              <th className="py-2.5 px-3">Ambulance Type</th>
                              <th className="py-2.5 px-3">Owned Vehicles</th>
                              <th className="py-2.5 px-3">Daily Available</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {selectedApp.data.fleet_capacities.map((fc, idx) => (
                              <tr key={idx} className="hover:bg-slate-50">
                                <td className="py-2.5 px-3 font-semibold text-blue-700">{renderVal(fc.ambulance_type)}</td>
                                <td className="py-2.5 px-3 text-slate-700">{renderVal(fc.owned)}</td>
                                <td className="py-2.5 px-3 text-emerald-700 font-semibold">{renderVal(fc.available_daily)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-3 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                    )}
                  </div>
                </div>
              )}

              {/* FLEET STEP 3: RIDES & CLIENTS */}
              {activeTab === 'step3' && (
                <div className="space-y-4">
                  {/* Monthly Rides Metrics 6-Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5">
                    <div className="p-3 rounded-xl border border-slate-200/80 bg-slate-50/50">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">COMPLETED RIDES</span>
                      <p className="font-semibold text-slate-800 text-sm mt-0.5">{renderVal(selectedApp.data?.monthly_rides?.completed_rides)}</p>
                    </div>
                    <div className="p-3 rounded-xl border border-slate-200/80 bg-slate-50/50">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">ENQUIRIES</span>
                      <p className="font-semibold text-slate-800 text-sm mt-0.5">{renderVal(selectedApp.data?.monthly_rides?.enquiries)}</p>
                    </div>
                    <div className="p-3 rounded-xl border border-slate-200/80 bg-slate-50/50">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">LOST RIDES</span>
                      <p className="font-semibold text-red-600 text-sm mt-0.5">{renderVal(selectedApp.data?.monthly_rides?.lost_rides)}</p>
                    </div>
                    <div className="p-3 rounded-xl border border-slate-200/80 bg-slate-50/50">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">AVG FARE</span>
                      <p className="font-semibold text-emerald-700 text-sm mt-0.5">
                        {selectedApp.data?.monthly_rides?.average_fare ? `₹${selectedApp.data.monthly_rides.average_fare}` : '-'}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl border border-slate-200/80 bg-slate-50/50">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">AVG TRIP DIST</span>
                      <p className="font-semibold text-blue-700 text-sm mt-0.5">
                        {selectedApp.data?.monthly_rides?.average_trip_km ? `${selectedApp.data.monthly_rides.average_trip_km} km` : '-'}
                      </p>
                    </div>
                    <div className="p-3 rounded-xl border border-slate-200/80 bg-slate-50/50">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">CONFIDENCE</span>
                      <p className="font-semibold text-purple-700 text-sm mt-0.5">{renderVal(selectedApp.data?.monthly_rides?.data_confidence)}</p>
                    </div>
                  </div>

                  {/* Lost Ride Reasons */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-2">
                    <span className="text-xs font-semibold text-slate-800 block">Primary Reasons for Lost or Missed Rides:</span>
                    {Array.isArray(selectedApp.data?.lost_ride_reasons) && selectedApp.data.lost_ride_reasons.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {selectedApp.data.lost_ride_reasons.map((r, rIdx) => (
                          <span key={rIdx} className="px-2.5 py-1 rounded-xl bg-amber-50 text-amber-800 border border-amber-200 text-xs font-medium">
                            ⚠️ {r}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="p-2 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                    )}
                  </div>

                  {/* Top Clients Table */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                    <span className="text-xs font-semibold text-slate-800 block">Top Institutional & Corporate Hospital Clients:</span>
                    {Array.isArray(selectedApp.data?.top_clients) && selectedApp.data.top_clients.length > 0 && selectedApp.data.top_clients.some(c => c.client) ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-slate-200 text-slate-500 font-semibold uppercase text-[10px]">
                              <th className="py-2.5 px-3">Client / Institution</th>
                              <th className="py-2.5 px-3">Type</th>
                              <th className="py-2.5 px-3">Area</th>
                              <th className="py-2.5 px-3">Rides / Month</th>
                              <th className="py-2.5 px-3">Avg Fare</th>
                              <th className="py-2.5 px-3">Payment Term</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {selectedApp.data.top_clients.filter(c => c.client).map((c, cIdx) => (
                              <tr key={cIdx} className="hover:bg-slate-50">
                                <td className="py-2.5 px-3 font-semibold text-slate-800">{renderVal(c.client)}</td>
                                <td className="py-2.5 px-3 text-slate-600">{renderVal(c.type)}</td>
                                <td className="py-2.5 px-3 text-slate-600">{renderVal(c.area)}</td>
                                <td className="py-2.5 px-3 text-blue-700 font-semibold">{renderVal(c.rides_month)}</td>
                                <td className="py-2.5 px-3 text-emerald-700 font-semibold">{c.average_fare ? `₹${c.average_fare}` : '-'}</td>
                                <td className="py-2.5 px-3 text-slate-600">{renderVal(c.payment)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-3 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                    )}
                  </div>

                  {/* Long Distance Corridors */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                    <span className="text-xs font-semibold text-slate-800 block">Frequent Intercity & Long-Distance Corridors:</span>
                    {Array.isArray(selectedApp.data?.long_distance_corridors) && selectedApp.data.long_distance_corridors.length > 0 && selectedApp.data.long_distance_corridors.some(l => l.pickup || l.destination) ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-slate-200 text-slate-500 font-semibold uppercase text-[10px]">
                              <th className="py-2.5 px-3">Pickup Location</th>
                              <th className="py-2.5 px-3">Destination City</th>
                              <th className="py-2.5 px-3">Trips / Month</th>
                              <th className="py-2.5 px-3">Est Distance</th>
                              <th className="py-2.5 px-3">Typical Fare</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {selectedApp.data.long_distance_corridors.filter(l => l.pickup || l.destination).map((ld, ldIdx) => (
                              <tr key={ldIdx} className="hover:bg-slate-50">
                                <td className="py-2.5 px-3 font-semibold text-slate-800">{renderVal(ld.pickup)}</td>
                                <td className="py-2.5 px-3 font-semibold text-blue-700">→ {renderVal(ld.destination)}</td>
                                <td className="py-2.5 px-3 text-slate-700">{renderVal(ld.trips_month)}</td>
                                <td className="py-2.5 px-3 text-slate-600">{ld.km ? `${ld.km} km` : '-'}</td>
                                <td className="py-2.5 px-3 font-semibold text-emerald-700">{ld.typical_rate ? `₹${ld.typical_rate}` : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-3 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                    )}
                  </div>
                </div>
              )}

              {/* FLEET STEP 4: RATES */}
              {activeTab === 'step4' && (
                <div className="space-y-4">
                  {/* If City Rate Cards exist */}
                  {Array.isArray(selectedApp.data?.city_rate_cards) && selectedApp.data.city_rate_cards.length > 0 ? (
                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-4">
                      <div className="flex items-center justify-between pb-3 border-b border-slate-100 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                            <DollarSign className="w-4 h-4" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-slate-800 text-xs sm:text-sm">
                              City Rate Cards & Distance Slabs ({selectedApp.data.city_rate_cards.length} Cities)
                            </h4>
                            <p className="text-[11px] text-slate-500">Tiered distance fares, add-ons and charges</p>
                          </div>
                        </div>

                        {/* City selection pills */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {selectedApp.data.city_rate_cards.map((c, cIdx) => (
                            <button
                              key={c.id || cIdx}
                              type="button"
                              onClick={() => setAdminRateCityIdx(cIdx)}
                              className={`px-3 py-1 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                                adminRateCityIdx === cIdx
                                  ? 'bg-blue-600 text-white shadow-2xs'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              {c.selectedCity || `City ${cIdx + 1}`}
                            </button>
                          ))}
                        </div>
                      </div>

                      {(() => {
                        const currentCity = selectedApp.data.city_rate_cards[adminRateCityIdx] || selectedApp.data.city_rate_cards[0];
                        if (!currentCity) return null;

                        return (
                          <div className="space-y-4">
                            {/* Ambulance Fares Table */}
                            <div className="space-y-2">
                              <span className="text-xs font-bold text-slate-800 block">
                                Ambulance Fares ({currentCity.selectedCity || 'Selected City'}):
                              </span>
                              {Array.isArray(currentCity.ambulanceFares) && currentCity.ambulanceFares.length > 0 ? (
                                <div className="overflow-x-auto border border-slate-200 rounded-xl">
                                  <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                      <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-600 font-semibold whitespace-nowrap">
                                        <th className="py-2.5 px-3 w-10 text-center">#</th>
                                        <th className="py-2.5 px-3 min-w-[140px]">Ambulance Type</th>
                                        <th className="py-2.5 px-3 min-w-[130px]">Vehicle Type</th>
                                        <th className="py-2.5 px-3 min-w-[160px]">Equipment / Staffing</th>
                                        {DISTANCE_SLABS.map((slab) => (
                                          <th key={slab.key} className="py-2.5 px-2 min-w-[85px] text-center font-semibold text-slate-600">
                                            {slab.label}
                                          </th>
                                        ))}
                                        <th className="py-2.5 px-3 min-w-[120px] text-center font-semibold text-slate-700">Outstation (₹/km)</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 font-medium bg-white">
                                      {currentCity.ambulanceFares.map((fare, fIdx) => (
                                        <tr key={fare.id || fIdx} className="hover:bg-slate-50">
                                          <td className="py-2.5 px-3 text-center text-slate-400">{fIdx + 1}</td>
                                          <td className="py-2.5 px-3 font-semibold text-blue-700">{renderVal(fare.ambulance_type)}</td>
                                          <td className="py-2.5 px-3 text-slate-700">{renderVal(fare.vehicle_type)}</td>
                                          <td className="py-2.5 px-3 text-slate-500">{renderVal(fare.equipment)}</td>
                                          {DISTANCE_SLABS.map((slab) => (
                                            <td key={slab.key} className="py-2.5 px-2 text-center text-slate-800 font-mono">
                                              {fare.slabs?.[slab.key] ? `₹${fare.slabs[slab.key]}` : '-'}
                                            </td>
                                          ))}
                                          <td className="py-2.5 px-3 text-center font-bold text-emerald-700 font-mono">
                                            {fare.outstation_rate ? `₹${fare.outstation_rate}/km` : '-'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-xs text-slate-400 italic">No ambulance fares added.</p>
                              )}
                            </div>

                            {/* Add-on Services Table */}
                            {Array.isArray(currentCity.addons) && currentCity.addons.length > 0 && currentCity.addons.some(a => a.addon_type) && (
                              <div className="space-y-2">
                                <span className="text-xs font-bold text-slate-800 block">
                                  Add-on Services:
                                </span>
                                <div className="overflow-x-auto border border-slate-200 rounded-xl">
                                  <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                      <tr className="bg-slate-50/80 border-b border-slate-200 text-slate-600 font-semibold whitespace-nowrap">
                                        <th className="py-2.5 px-3 w-10 text-center">#</th>
                                        <th className="py-2.5 px-3 min-w-[150px]">Add-on Service</th>
                                        <th className="py-2.5 px-3 min-w-[180px]">Description</th>
                                        {DISTANCE_SLABS.map((slab) => (
                                          <th key={slab.key} className="py-2.5 px-2 min-w-[85px] text-center font-semibold text-slate-600">
                                            {slab.label}
                                          </th>
                                        ))}
                                        <th className="py-2.5 px-3 min-w-[120px] text-center font-semibold text-slate-700">Outstation (₹/km)</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 font-medium bg-white">
                                      {currentCity.addons.filter(a => a.addon_type).map((add, aIdx) => (
                                        <tr key={add.id || aIdx} className="hover:bg-slate-50">
                                          <td className="py-2.5 px-3 text-center text-slate-400">{aIdx + 1}</td>
                                          <td className="py-2.5 px-3 font-semibold text-slate-800">{renderVal(add.addon_type)}</td>
                                          <td className="py-2.5 px-3 text-slate-500">{renderVal(add.description)}</td>
                                          {DISTANCE_SLABS.map((slab) => (
                                            <td key={slab.key} className="py-2.5 px-2 text-center text-slate-800 font-mono">
                                              {add.slabs?.[slab.key] ? `₹${add.slabs[slab.key]}` : '-'}
                                            </td>
                                          ))}
                                          <td className="py-2.5 px-3 text-center font-bold text-emerald-700 font-mono">
                                            {add.outstation_rate ? `₹${add.outstation_rate}/km` : '-'}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* Other & Waiting Charges Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/50 space-y-2">
                                <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block">Other Charges</span>
                                {Array.isArray(currentCity.otherCharges) && currentCity.otherCharges.length > 0 && currentCity.otherCharges.some(o => o.item_service) ? (
                                  <div className="space-y-1.5">
                                    {currentCity.otherCharges.filter(o => o.item_service).map((oc, oIdx) => (
                                      <div key={oc.id || oIdx} className="flex items-center justify-between py-1 px-2.5 bg-white rounded-lg border border-slate-200 text-xs">
                                        <span className="font-medium text-slate-800">{oc.item_service}</span>
                                        <span className="font-mono text-emerald-700 font-semibold">
                                          12h: ₹{oc.charge_12hr || '0'} | 24h: ₹{oc.charge_24hr || '0'}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-slate-400 italic">None specified</p>
                                )}
                              </div>

                              <div className="p-3.5 rounded-xl border border-slate-200 bg-slate-50/50 space-y-2">
                                <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider block">Waiting Charges</span>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div className="p-2 bg-white rounded-lg border border-slate-200">
                                    <span className="text-[10px] text-slate-400 block font-semibold">FREE WAITING TIME</span>
                                    <span className="font-semibold text-slate-800">{currentCity.waitingCharges?.free_waiting_minutes ? `${currentCity.waitingCharges.free_waiting_minutes} mins` : '-'}</span>
                                  </div>
                                  <div className="p-2 bg-white rounded-lg border border-slate-200">
                                    <span className="text-[10px] text-slate-400 block font-semibold">CHARGE PER HOUR</span>
                                    <span className="font-semibold text-emerald-700 font-mono">{currentCity.waitingCharges?.rate_per_hour ? `₹${currentCity.waitingCharges.rate_per_hour}/hr` : '-'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Rate Card Notes */}
                            {currentCity.notes && (
                              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs text-slate-600">
                                <strong className="font-semibold text-slate-800 block mb-0.5">Rate Card Notes:</strong>
                                {currentCity.notes}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ) : ((Array.isArray(selectedApp.data?.rate_cards) && selectedApp.data.rate_cards.length > 0) || selectedApp.data?.active_rate_card?.base_rate) ? (
                    /* Fallback to legacy flat rate cards */
                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                          <DollarSign className="w-4 h-4" />
                        </div>
                        <h4 className="font-semibold text-slate-800 text-xs sm:text-sm">Commercial Rate Cards & Tariffs</h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="border-b border-slate-200 text-slate-500 font-semibold uppercase text-[10px]">
                              <th className="py-2.5 px-3">Ambulance Type</th>
                              <th className="py-2.5 px-3">Base Fare</th>
                              <th className="py-2.5 px-3">Per KM Rate</th>
                              <th className="py-2.5 px-3">Other / Waiting Charges</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 font-medium">
                            {(Array.isArray(selectedApp.data?.rate_cards) && selectedApp.data.rate_cards.length > 0
                              ? selectedApp.data.rate_cards
                              : [selectedApp.data?.active_rate_card]
                            ).filter(Boolean).map((rc, rcIdx) => (
                              <tr key={rcIdx} className="hover:bg-slate-50">
                                <td className="py-2.5 px-3 font-semibold text-slate-800">{renderVal(rc.ambulance_type || 'Not specified')}</td>
                                <td className="py-2.5 px-3 font-semibold text-emerald-700">{rc.base_rate ? `₹${rc.base_rate}` : '-'}</td>
                                <td className="py-2.5 px-3 text-slate-700">{rc.per_km ? `₹${rc.per_km} / km` : '-'}</td>
                                <td className="py-2.5 px-3 text-slate-500">{renderVal(rc.others)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 text-center text-slate-400 font-medium bg-slate-50 rounded-xl border border-slate-200">
                      No rate card pricing entered.
                    </div>
                  )}
                </div>
              )}

              {/* FLEET STEP 5: ADD AMBULANCES */}
              {activeTab === 'step5' && (
                <div className="space-y-4">
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                          <Truck className="w-4 h-4" />
                        </div>
                        <h4 className="font-semibold text-slate-800 text-xs sm:text-sm">
                          Enrolled Ambulance Fleet & Vehicle Profiles
                        </h4>
                      </div>
                      {Array.isArray(selectedApp.data?.ambulances) && selectedApp.data.ambulances.length > 0 && (
                        <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-semibold text-xs border border-blue-200">
                          {selectedApp.data.ambulances.filter(a => a.registration_number || a.make_model || a.vehicle_code).length} Vehicles Enrolled
                        </span>
                      )}
                    </div>

                    {Array.isArray(selectedApp.data?.ambulances) && selectedApp.data.ambulances.length > 0 && selectedApp.data.ambulances.some(a => a.registration_number || a.make_model || a.vehicle_code) ? (
                      <div className="space-y-4">
                        {selectedApp.data.ambulances.filter(a => a.registration_number || a.make_model || a.vehicle_code).map((amb, aIdx) => (
                          <div key={amb.id || aIdx} className="p-4 rounded-2xl border border-slate-200 bg-slate-50/50 space-y-3.5">
                            {/* Vehicle Profile Row */}
                            <div className="flex items-center justify-between flex-wrap gap-2 pb-2.5 border-b border-slate-200/70">
                              <div className="flex items-center gap-2.5 flex-wrap">
                                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center shrink-0">
                                  {aIdx + 1}
                                </span>
                                {amb.vehicle_code && (
                                  <span className="px-2 py-0.5 rounded bg-slate-200 text-slate-800 font-mono font-semibold text-xs">
                                    {amb.vehicle_code}
                                  </span>
                                )}
                                <span className="font-mono font-bold text-blue-700 text-sm">
                                  {renderVal(amb.registration_number)}
                                </span>
                                <span className="px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold">
                                  {renderVal(amb.ambulance_type)}
                                </span>
                                {amb.ownership && (
                                  <span className="px-2 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 text-[11px] font-medium">
                                    {amb.ownership}
                                  </span>
                                )}
                              </div>

                              <div className="text-right text-xs">
                                <span className="font-semibold text-slate-800">{renderVal(amb.make_model)}</span>
                                {amb.manufacture_year && (
                                  <span className="text-slate-500 ml-1.5">({amb.manufacture_year})</span>
                                )}
                              </div>
                            </div>

                            {/* Pickup Stands Table */}
                            {Array.isArray(amb.pickup_stands) && amb.pickup_stands.length > 0 && (
                              <div className="space-y-1.5">
                                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">
                                  Pickup Stands & Service Coverage:
                                </span>
                                <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white">
                                  <table className="w-full text-left text-xs border-collapse">
                                    <thead>
                                      <tr className="bg-slate-50 text-slate-600 font-semibold border-b border-slate-200">
                                        <th className="py-2 px-3 w-8 text-center">#</th>
                                        <th className="py-2 px-3 min-w-[120px]">Stand Name</th>
                                        <th className="py-2 px-3 min-w-[160px]">Map Location</th>
                                        <th className="py-2 px-3 min-w-[100px]">Area</th>
                                        <th className="py-2 px-3 min-w-[80px]">PIN</th>
                                        <th className="py-2 px-3 min-w-[90px]">State</th>
                                        <th className="py-2 px-3 min-w-[90px]">City</th>
                                        <th className="py-2 px-3 min-w-[80px]">Radius</th>
                                        <th className="py-2 px-3 min-w-[90px]">Intercity</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                      {amb.pickup_stands.map((stand, sIdx) => (
                                        <tr key={stand.id || sIdx} className="hover:bg-slate-50/60">
                                          <td className="py-2 px-3 text-center text-slate-400">{sIdx + 1}</td>
                                          <td className="py-2 px-3 font-semibold text-slate-800">{renderVal(stand.stand_name)}</td>
                                          <td className="py-2 px-3 text-slate-600">
                                            <div className="flex items-center gap-1">
                                              <MapPin className="w-3 h-3 text-blue-600 shrink-0" />
                                              <span>{renderVal(stand.map_location)}</span>
                                            </div>
                                          </td>
                                          <td className="py-2 px-3 text-slate-600">{renderVal(stand.area)}</td>
                                          <td className="py-2 px-3 font-mono text-slate-600">{renderVal(stand.pincode)}</td>
                                          <td className="py-2 px-3 text-slate-600">{renderVal(stand.state)}</td>
                                          <td className="py-2 px-3 text-slate-600">{renderVal(stand.city)}</td>
                                          <td className="py-2 px-3 text-slate-800 font-semibold">{stand.service_radius_km ? `${stand.service_radius_km} km` : '-'}</td>
                                          <td className="py-2 px-3 font-medium text-emerald-700">{renderVal(stand.intercity_available)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* Capabilities: Medical Equipment & Technology Badges */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                              {/* Medical Equipment */}
                              <div className="p-2.5 rounded-xl bg-white border border-slate-200/80 space-y-1.5">
                                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                                  Medical Equipment ({amb.medical_equipment?.length || 0}):
                                </span>
                                {Array.isArray(amb.medical_equipment) && amb.medical_equipment.length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {amb.medical_equipment.map((eq, eIdx) => (
                                      <span key={eIdx} className="px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200/60 text-[11px] font-medium">
                                        ✓ {eq}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400 italic">None specified</span>
                                )}
                              </div>

                              {/* Technology */}
                              <div className="p-2.5 rounded-xl bg-white border border-slate-200/80 space-y-1.5">
                                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                                  Technology & Connected Devices ({amb.technology?.length || 0}):
                                </span>
                                {Array.isArray(amb.technology) && amb.technology.length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5">
                                    {amb.technology.map((tech, tIdx) => (
                                      <span key={tIdx} className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200/60 text-[11px] font-medium">
                                        ⚡ {tech}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400 italic">None specified</span>
                                )}
                              </div>
                            </div>

                            {/* All 7 Vehicle Compliance Documents Grid */}
                            <div className="pt-2 border-t border-slate-200/80 space-y-1.5">
                              <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider block">
                                Vehicle Compliance & Regulatory Documents (7 items):
                              </span>
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                                {renderDocBadge('RC (Registration)', amb.documents?.rc)}
                                {renderDocBadge('Insurance Policy', amb.documents?.insurance)}
                                {renderDocBadge('Fitness Certificate', amb.documents?.fitness_certificate || amb.documents?.fitness)}
                                {renderDocBadge('Permit', amb.documents?.permit)}
                                {renderDocBadge('PUC Certificate', amb.documents?.puc)}
                                {renderDocBadge('Driver Licence', amb.documents?.driver_licence || amb.documents?.driver_license)}
                                {renderDocBadge('Ambulance Reg / Approval', amb.documents?.ambulance_registration)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-3 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                    )}
                  </div>
                </div>
              )}

              {/* FLEET STEP 6: REVIEW & SUBMISSION */}
              {activeTab === 'step6' && (
                <div className="space-y-4">
                  {/* Submission Details */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-slate-50/70 space-y-2">
                    <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
                      APPLICATION SUBMISSION ATTESTATION
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Application Status</span>
                        <p className="font-semibold text-slate-800 mt-0.5">{selectedApp.status}</p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Submission Date</span>
                        <p className="font-semibold text-slate-800 mt-0.5">
                          {selectedApp.submitted_at ? new Date(selectedApp.submitted_at).toLocaleString() : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Accuracy Declaration</span>
                        <p className="font-semibold text-emerald-700 mt-0.5">
                          {selectedApp.data?.confirmed_accurate || selectedApp.confirmed_accurate ? 'Confirmed & Attested' : '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Authorized Signatory / Administrator */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                    <span className="text-xs font-semibold text-slate-800 block">Authorized Fleet Signatory:</span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Signatory / Operator Name</span>
                        <p className="font-semibold text-slate-800 mt-0.5">{renderVal(selectedApp.data?.admin_name || selectedApp.data?.operator_name || selectedApp.name)}</p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Official Email</span>
                        <p className="font-semibold text-slate-800 mt-0.5">{renderVal(selectedApp.data?.admin_email || selectedApp.data?.contact_info || selectedApp.data?.email)}</p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Contact Phone</span>
                        <p className="font-semibold text-slate-800 mt-0.5">{renderVal(selectedApp.data?.admin_phone || selectedApp.data?.emergency_contact || selectedApp.data?.mobile)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Fleet Operations & Readiness Summary */}
                  {(() => {
                    const ambList = ensureArray(selectedApp.data?.ambulances);
                    const rateList = ensureArray(selectedApp.data?.rate_cards);
                    return (
                      <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                        <span className="text-xs font-semibold text-slate-800 block">Fleet Readiness & Operations Snapshot:</span>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="p-3 rounded-xl bg-blue-50/60 border border-blue-100">
                            <span className="text-[10px] text-blue-700 uppercase font-semibold block">Configured Ambulances</span>
                            <span className="text-lg font-bold text-blue-900 mt-0.5 block">{ambList.length}</span>
                            <span className="text-[10px] text-blue-600">Vehicles in Step 5</span>
                          </div>
                          <div className="p-3 rounded-xl bg-indigo-50/60 border border-indigo-100">
                            <span className="text-[10px] text-indigo-700 uppercase font-semibold block">Rate Cards</span>
                            <span className="text-lg font-bold text-indigo-900 mt-0.5 block">{rateList.length}</span>
                            <span className="text-[10px] text-indigo-600">Categories in Step 4</span>
                          </div>
                          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80">
                            <span className="text-[10px] text-slate-500 uppercase font-semibold block">Primary Service Area</span>
                            <span className="text-xs font-semibold text-slate-800 mt-1 block truncate">
                              {renderVal(selectedApp.data?.service_area || selectedApp.city || selectedApp.data?.city)}
                            </span>
                            <span className="text-[10px] text-slate-400">Coverage zone</span>
                          </div>
                          <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80">
                            <span className="text-[10px] text-slate-500 uppercase font-semibold block">Emergency 24x7 Line</span>
                            <span className="text-xs font-semibold text-slate-800 mt-1 block truncate">
                              {renderVal(selectedApp.data?.emergency_contact || selectedApp.data?.admin_phone)}
                            </span>
                            <span className="text-[10px] text-slate-400">Dispatch helpline</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Audit Verification Stamp */}
                  <div className="p-4 rounded-2xl border border-emerald-200/80 bg-emerald-50/50 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-emerald-950">Fleet Verification & Compliance Attestation Confirmed</p>
                      <p className="text-[11px] text-emerald-800/80 mt-0.5 leading-relaxed">
                        The fleet operator has digitally confirmed that all vehicle registration documents, driver credentials, medical equipment inventories, and service tariffs submitted in this application are authentic and compliant with local ambulance transport regulations.
                      </p>
                    </div>
                  </div>

                  {/* Proceed to Phone Verification & Activation Form Banner */}
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-2xs">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0">
                        <PhoneCall className="w-5 h-5 animate-pulse" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-slate-800 block">
                          Next: Phone Verification & Partner Activation
                        </span>
                        <p className="text-[11px] text-slate-600">
                          Review is complete. Call the fleet operator to verify registration details, configure credentials and activate platform access.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveTab('step7')}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs shadow-xs transition-colors shrink-0 cursor-pointer"
                    >
                      <span>Open Activation Form</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* FLEET STEP 7: ADMIN PHONE VERIFICATION & ACTIVATION FORM (Matching User Image 3) */}
              {activeTab === 'step7' && (
                <div className="space-y-4 animate-fade-in">
                  {/* Call Desk Header Banner */}
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-50 via-sky-50 to-indigo-50 border border-blue-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-2xs">
                        <PhoneCall className="w-5 h-5 animate-pulse" />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 block">
                          Super Admin Phone Verification Desk
                        </span>
                        <p className="text-xs text-slate-800 font-semibold">
                          Call operator at <span className="font-mono text-blue-700">{activationForm.admin_phone || selectedApp.data?.admin_phone || selectedApp.data?.mobile || 'Phone not set'}</span> to verify details & configure credentials.
                        </p>
                      </div>
                    </div>
                    {(activationForm.admin_phone || selectedApp.data?.admin_phone) && (
                      <a
                        href={`tel:${activationForm.admin_phone || selectedApp.data?.admin_phone}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-blue-200 text-blue-700 text-xs font-bold hover:bg-blue-100/50 shadow-2xs transition-colors shrink-0"
                      >
                        <Phone className="w-3.5 h-3.5 text-blue-600" />
                        <span>Call Applicant</span>
                      </a>
                    )}
                  </div>

                  {/* Header Title as per Image 3 */}
                  <div className="pb-1">
                    <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest block mb-1">
                      PARTNER REGISTRATION (SUPER ADMIN VERIFICATION)
                    </span>
                    <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
                      Become a RISA partner
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Confirm and complete details about this fleet operator before approving live platform access.
                    </p>
                  </div>

                  {/* Card 1: FLEET OPERATOR INFORMATION */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <Truck className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        FLEET OPERATOR INFORMATION
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Fleet Operator Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Enter fleet operator name"
                          value={activationForm.name || ''}
                          onChange={(e) => {
                            setActivationForm((p) => ({ ...p, name: e.target.value }));
                            if (formErrors.name) setFormErrors((p) => ({ ...p, name: '' }));
                          }}
                          className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                            formErrors.name ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {formErrors.name && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.name}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Contact Information <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Contact phone or information"
                          value={activationForm.contact_info || ''}
                          onChange={(e) => {
                            setActivationForm((p) => ({ ...p, contact_info: e.target.value }));
                            if (formErrors.contact_info) setFormErrors((p) => ({ ...p, contact_info: '' }));
                          }}
                          className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                            formErrors.contact_info ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {formErrors.contact_info && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.contact_info}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Card 2: ADMIN INFORMATION */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <User className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        ADMIN INFORMATION
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Admin Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Enter admin name"
                          value={activationForm.admin_name || ''}
                          onChange={(e) => {
                            setActivationForm((p) => ({ ...p, admin_name: e.target.value }));
                            if (formErrors.admin_name) setFormErrors((p) => ({ ...p, admin_name: '' }));
                          }}
                          className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                            formErrors.admin_name ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {formErrors.admin_name && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.admin_name}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Admin Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          placeholder="Enter official partner admin email"
                          value={activationForm.admin_email || ''}
                          onChange={(e) => {
                            setActivationForm((p) => ({ ...p, admin_email: e.target.value }));
                            if (formErrors.admin_email) setFormErrors((p) => ({ ...p, admin_email: '' }));
                          }}
                          className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                            formErrors.admin_email ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {activationForm.admin_email?.includes('command@') && (
                          <p className="text-[11px] text-blue-700 bg-blue-50/70 border border-blue-200/80 rounded-lg p-2 mt-1.5 leading-relaxed">
                            💡 <strong>Tip:</strong> <code>{activationForm.admin_email}</code> is your Command Center account. Enter the fleet operator&apos;s own email (e.g. <code>fleet@example.com</code>) so the partner team can log in directly.
                          </p>
                        )}
                        {formErrors.admin_email && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.admin_email}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Admin Phone <span className="text-red-500">*</span>
                      </label>
                      <div className="flex rounded-xl border border-slate-200 overflow-hidden focus-within:ring-1 focus-within:ring-blue-600 focus-within:border-blue-600">
                        <span className="px-3 py-2.5 bg-slate-50 border-r border-slate-200 text-xs font-semibold text-slate-600 select-none">
                          IN +91
                        </span>
                        <input
                          type="tel"
                          maxLength={10}
                          placeholder="Enter 10-digit mobile number"
                          value={activationForm.admin_phone || ''}
                          onChange={(e) => {
                            const clean = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setActivationForm((p) => ({ ...p, admin_phone: clean }));
                            if (formErrors.admin_phone) setFormErrors((p) => ({ ...p, admin_phone: '' }));
                          }}
                          className="flex-1 px-3.5 py-2.5 bg-white text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
                        />
                      </div>
                      {formErrors.admin_phone && (
                        <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.admin_phone}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-medium text-slate-600">
                            Temporary Password <span className="text-red-500">*</span>
                          </label>
                          <button
                            type="button"
                            onClick={handleGeneratePassword}
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:text-blue-700 cursor-pointer"
                          >
                            <Sparkles className="w-3 h-3 text-blue-600" />
                            <span>Generate</span>
                          </button>
                        </div>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Set temporary password"
                            value={activationForm.admin_password}
                            onChange={(e) => {
                              setActivationForm((p) => ({ ...p, admin_password: e.target.value }));
                              if (formErrors.admin_password) setFormErrors((p) => ({ ...p, admin_password: '' }));
                            }}
                            className={`w-full pr-10 px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all font-mono ${
                              formErrors.admin_password ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((p) => !p)}
                            className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                          >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {formErrors.admin_password && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.admin_password}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Confirm Temporary Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Confirm temporary password"
                            value={activationForm.confirm_password}
                            onChange={(e) => {
                              setActivationForm((p) => ({ ...p, confirm_password: e.target.value }));
                              if (formErrors.confirm_password) setFormErrors((p) => ({ ...p, confirm_password: '' }));
                            }}
                            className={`w-full pr-10 px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all font-mono ${
                              formErrors.confirm_password ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword((p) => !p)}
                            className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {formErrors.confirm_password && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.confirm_password}</p>
                        )}
                      </div>
                    </div>

                    <div className="p-3 rounded-xl bg-amber-50/80 border border-amber-200 text-amber-800 text-[11px] flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <span>This is a temporary password. The user will be required to change it on first login.</span>
                      </div>
                      {passwordCopied && (
                        <span className="text-emerald-700 font-bold text-[10px] bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                          Password Generated!
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card 3: BUSINESS INFORMATION (OPTIONAL) */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <FileText className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        BUSINESS INFORMATION (OPTIONAL)
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Business License
                        </label>
                        <input
                          type="text"
                          placeholder="Business license number"
                          value={activationForm.license_number || ''}
                          onChange={(e) => setActivationForm((p) => ({ ...p, license_number: e.target.value }))}
                          className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Emergency Contact
                        </label>
                        <input
                          type="text"
                          placeholder="Emergency contact number"
                          value={activationForm.emergency_contact || ''}
                          onChange={(e) => setActivationForm((p) => ({ ...p, emergency_contact: e.target.value }))}
                          className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Address
                      </label>
                      <input
                        type="text"
                        placeholder="Enter complete address"
                        value={activationForm.address || ''}
                        onChange={(e) => setActivationForm((p) => ({ ...p, address: e.target.value }))}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">City</label>
                        <input
                          type="text"
                          placeholder="City"
                          value={activationForm.city || ''}
                          onChange={(e) => setActivationForm((p) => ({ ...p, city: e.target.value }))}
                          className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">State</label>
                        <input
                          type="text"
                          placeholder="State"
                          value={activationForm.state || ''}
                          onChange={(e) => setActivationForm((p) => ({ ...p, state: e.target.value }))}
                          className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Pincode</label>
                        <input
                          type="text"
                          maxLength={6}
                          placeholder="Pincode"
                          value={activationForm.pincode || ''}
                          onChange={(e) => setActivationForm((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, '') }))}
                          className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Service Area
                      </label>
                      <input
                        type="text"
                        placeholder="Service area coverage"
                        value={activationForm.service_area || ''}
                        onChange={(e) => setActivationForm((p) => ({ ...p, service_area: e.target.value }))}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"
                      />
                    </div>
                  </div>

                  {/* Submit / Activate Button Banner */}
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleSaveAndApprove}
                      disabled={actionLoading || selectedApp.status !== 'SUBMITTED'}
                      className="w-full py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs sm:text-sm shadow-md transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 stroke-[2.5]" />}
                      <span>Approve & Activate Fleet Partner</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* HOSPITAL STEP 1: HOSPITAL PROFILE */}
              {activeTab === 'step1' && (
                <div className="space-y-4">
                  {/* 4-Grid Stats Row */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
                    <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <Building2 className="w-5 h-5" />
                      </div>
                      <div className="truncate">
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
                          LICENSE / REG NO
                        </span>
                        <p className="font-semibold text-slate-800 text-xs sm:text-sm mt-0.5 truncate">
                          {renderVal(selectedApp.license_number || selectedApp.data?.license_number)}
                        </p>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div className="truncate">
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
                          CITY, STATE
                        </span>
                        <p className="font-semibold text-slate-700 text-xs sm:text-sm mt-0.5 truncate">
                          {renderVal(selectedApp.city)}, {renderVal(selectedApp.state)}
                        </p>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <QrCode className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
                          PINCODE
                        </span>
                        <p className="font-semibold text-slate-700 text-xs sm:text-sm mt-0.5">
                          {renderVal(selectedApp.pincode)}
                        </p>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-2xl border border-slate-200/80 bg-slate-50/50 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                        <Bed className="w-5 h-5" />
                      </div>
                      <div className="truncate">
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
                          HOSPITAL TYPE
                        </span>
                        <p className="font-semibold text-blue-600 text-xs sm:text-sm mt-0.5 truncate">
                          {renderVal(selectedApp.data?.hospital_types)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Interactive Hospital Location Map Card */}
                  {renderMapBlock()}

                  {/* Admin Account & Linked Operator */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                          <User className="w-4 h-4" />
                        </div>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                          PRIMARY ADMIN ACCOUNT
                        </span>
                      </div>
                      <div className="pl-9 space-y-1">
                        <p className="font-semibold text-slate-800 text-sm">{renderVal(selectedApp.admin_name || selectedApp.data?.admin_name)}</p>
                        <p className="text-slate-500 text-xs flex items-center gap-1.5 font-normal">
                          <Mail className="w-3.5 h-3.5 text-slate-400" />
                          <span>{renderVal(selectedApp.admin_email || selectedApp.data?.admin_email || selectedApp.data?.email)}</span>
                        </p>
                        <p className="text-slate-500 text-xs flex items-center gap-1.5 font-normal">
                          <Phone className="w-3.5 h-3.5 text-slate-400" />
                          <span>{selectedApp.admin_phone_country_code || '+91'} {renderVal(selectedApp.admin_phone || selectedApp.data?.admin_phone || selectedApp.data?.mobile)}</span>
                        </p>
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                          <Truck className="w-4 h-4" />
                        </div>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                          LINKED FLEET OPERATOR
                        </span>
                      </div>
                      <div className="pl-9 space-y-1">
                        <p className="text-slate-700 text-sm font-normal">
                          Operator: <strong className="text-slate-800 font-semibold">{renderVal(selectedApp.data?.operator_name || selectedApp.operator_id)}</strong>
                        </p>
                        <p className="text-slate-500 text-xs pt-0.5">
                          Emergency Line: <span className="font-semibold text-slate-700">{renderVal(selectedApp.data?.mobile || selectedApp.admin_phone)}</span>
                        </p>
                        <p className="text-slate-500 text-xs pt-0.5">
                          Beds: <span className="font-semibold text-slate-700">{renderVal(selectedApp.data?.total_beds || selectedApp.data?.number_of_beds)}</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Step 1 Document Check */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-2.5">
                    <span className="text-xs font-semibold text-slate-800 block">Step 1 Verification Document:</span>
                    {renderDocBadge(
                      'Hospital Clinical Establishment Registration License',
                      selectedApp.data?.license_document || selectedApp.data?.documents?.registration_license || selectedApp.data?.documents?.clinical_license
                    )}
                  </div>
                </div>
              )}

              {/* HOSPITAL STEP 2: FACILITIES & KEY CONTACTS */}
              {activeTab === 'step2' && (
                <div className="space-y-4">
                  {/* Bed Capacity 3-Grid */}
                  <div className="grid grid-cols-3 gap-3.5">
                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-blue-50/40 flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                        <Bed className="w-6 h-6" />
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">TOTAL BED CAPACITY</span>
                        <p className="font-semibold text-blue-700 text-xl mt-0.5">{renderVal(selectedApp.data?.total_beds || selectedApp.data?.number_of_beds)}</p>
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-indigo-50/40 flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
                        <Activity className="w-6 h-6" />
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">ICU BEDS</span>
                        <p className="font-semibold text-indigo-700 text-xl mt-0.5">{renderVal(selectedApp.data?.icu_beds)}</p>
                      </div>
                    </div>

                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-emerald-50/40 flex items-center gap-3.5">
                      <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                        <AlertCircle className="w-6 h-6" />
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">EMERGENCY BEDS</span>
                        <p className="font-semibold text-emerald-700 text-xl mt-0.5">{renderVal(selectedApp.data?.emergency_beds)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Emergency Facilities Badges */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-2.5">
                    <span className="text-xs font-semibold text-slate-800 block">Critical Care & Emergency Facilities:</span>
                    {Array.isArray(selectedApp.data?.emergency_facilities) && selectedApp.data.emergency_facilities.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedApp.data.emergency_facilities.map((fac, idx) => (
                          <span key={idx} className="px-3 py-1 rounded-xl bg-blue-50 text-blue-800 border border-blue-200 font-semibold text-xs flex items-center gap-1.5">
                            <Check className="w-3.5 h-3.5 text-blue-600" />
                            <span>{fac}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="p-2 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                    )}
                  </div>

                  {/* Contacts Roster */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3 font-sans">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                          <User className="w-4 h-4" />
                        </div>
                        <h4 className="font-semibold text-slate-800 text-sm">
                          Hospital Department Contact Persons
                        </h4>
                      </div>
                      {Array.isArray(selectedApp.data?.contacts) && selectedApp.data.contacts.length > 0 && selectedApp.data.contacts.some(c => c.name) && (
                        <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">
                          {selectedApp.data.contacts.filter(c => c.name).length} Contacts
                        </span>
                      )}
                    </div>

                    {Array.isArray(selectedApp.data?.contacts) && selectedApp.data.contacts.length > 0 && selectedApp.data.contacts.some(c => c.name) ? (
                      <div className="space-y-2">
                        {selectedApp.data.contacts.filter(c => c.name).map((c, idx) => {
                          const initials = getInitials(c.name);
                          const isPrimary = c.is_primary === 'Yes';
                          const isOps = (c.role || '').toLowerCase().includes('operat');
                          const isNursing = (c.role || '').toLowerCase().includes('nurs');

                          const avatarBg = isPrimary ? 'bg-blue-100 text-blue-700' : isOps ? 'bg-purple-100 text-purple-700' : 'bg-emerald-100 text-emerald-700';
                          const badgeBg = isPrimary ? 'bg-blue-50 text-blue-700 border-blue-200' : isOps ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200';
                          const badgeText = isPrimary ? 'PRIMARY' : isOps ? 'OPERATIONS' : isNursing ? 'NURSING' : (c.role || 'CONTACT').toUpperCase();

                          return (
                            <div
                              key={idx}
                              className="flex items-center justify-between p-3 rounded-2xl border border-slate-200/80 bg-white hover:bg-slate-50/60 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-full ${avatarBg} font-semibold text-xs flex items-center justify-center shrink-0`}>
                                  {initials}
                                </div>
                                <div>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="font-semibold text-slate-800 text-xs">{c.name}</span>
                                    <span className="text-slate-500 text-xs font-normal">({renderVal(c.role)})</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-[11px] text-slate-400 mt-0.5">
                                    <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" /> {renderVal(c.mobile)}</span>
                                    <span>•</span>
                                    <span className="flex items-center gap-1"><Mail className="w-3 h-3 text-slate-400" /> {renderVal(c.email)}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2.5 py-0.5 rounded-full border text-[10px] font-semibold ${badgeBg}`}>
                                  {badgeText}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-3 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                    )}
                  </div>
                </div>
              )}

              {/* HOSPITAL STEP 3: AMBULANCE REQUIREMENTS */}
              {activeTab === 'step3' && (
                <div className="space-y-4">
                  {/* Monthly Demands Table */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                    <span className="text-xs font-semibold text-slate-800 block">Monthly Ambulance Demand Breakdown:</span>
                    {Array.isArray(selectedApp.data?.monthly_demands) && selectedApp.data.monthly_demands.length > 0 && selectedApp.data.monthly_demands.some(md => md.month || md.total) ? (() => {
                      const nonTypeKeys = new Set(['month', 'period', 'total', 'inbound', 'outbound', 'unserved', 'id', 'key']);
                      const detectedKeysSet = new Set();
                      selectedApp.data.monthly_demands.forEach(row => {
                        if (row && typeof row === 'object') {
                          Object.keys(row).forEach(k => {
                            if (!nonTypeKeys.has(k) && row[k] !== undefined && row[k] !== '') {
                              detectedKeysSet.add(k);
                            }
                          });
                        }
                      });

                      const preferredOrder = ['acls', 'als', 'bls_big', 'bls_small', 'bls', 'icu', 'pt', 'other'];
                      const typeKeys = preferredOrder.filter(k => detectedKeysSet.has(k));
                      detectedKeysSet.forEach(k => {
                        if (!typeKeys.includes(k)) typeKeys.push(k);
                      });

                      if (typeKeys.length === 0) {
                        typeKeys.push('als', 'bls');
                      }

                      const getColLabel = (key) => {
                        const map = {
                          acls: 'ACLS',
                          als: 'ALS',
                          bls_big: 'BLS Big',
                          bls_small: 'BLS Small',
                          bls: 'BLS',
                          icu: 'ICU',
                          pt: 'PT',
                          other: 'Other'
                        };
                        return map[key.toLowerCase()] || key.replace(/_/g, ' ').toUpperCase();
                      };

                      const hasAmbulanceTypeCol = selectedApp.data.monthly_demands.some(md => md.ambulance_type);

                      return (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs">
                            <thead>
                              <tr className="border-b border-slate-200 text-slate-500 font-semibold uppercase text-[10px]">
                                <th className="py-2.5 px-3">Period</th>
                                {hasAmbulanceTypeCol && <th className="py-2.5 px-3 text-blue-700">Ambulance Type</th>}
                                <th className="py-2.5 px-3">Total Calls</th>
                                <th className="py-2.5 px-3">Inbound</th>
                                <th className="py-2.5 px-3">Outbound</th>
                                {!hasAmbulanceTypeCol && typeKeys.map(k => (
                                  <th key={k} className="py-2.5 px-3 text-blue-700">{getColLabel(k)}</th>
                                ))}
                                <th className="py-2.5 px-3 text-red-600">Unserved</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 font-medium">
                              {selectedApp.data.monthly_demands.map((md, idx) => (
                                <tr key={idx} className="hover:bg-slate-50">
                                  <td className="py-2.5 px-3 font-semibold text-slate-800">{renderVal(md.month || 'Not specified')}</td>
                                  {hasAmbulanceTypeCol && <td className="py-2.5 px-3 font-semibold text-slate-800">{renderVal(md.ambulance_type)}</td>}
                                  <td className="py-2.5 px-3 font-semibold text-blue-700">{renderVal(md.total)}</td>
                                  <td className="py-2.5 px-3 text-slate-700">{renderVal(md.inbound)}</td>
                                  <td className="py-2.5 px-3 text-slate-700">{renderVal(md.outbound)}</td>
                                  {!hasAmbulanceTypeCol && typeKeys.map(k => (
                                    <td key={k} className="py-2.5 px-3 text-slate-700">{renderVal(md[k])}</td>
                                  ))}
                                  <td className="py-2.5 px-3 text-red-600 font-semibold">{renderVal(md.unserved)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })() : (
                      <div className="p-3 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                    )}
                  </div>

                  {/* Peak Windows & Fleet Mix */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-1.5">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">PEAK EMERGENCY WINDOWS</span>
                      <p className="text-slate-800 font-semibold text-xs">
                        Peak Slots: {Array.isArray(selectedApp.data?.peak_time_slots) && selectedApp.data.peak_time_slots.length > 0 ? selectedApp.data.peak_time_slots.join(', ') : '-'}
                      </p>
                      {selectedApp.data?.custom_peak_time && (
                        <p className="text-slate-600 text-xs flex items-center gap-1.5 flex-wrap">
                          <span>Custom Peak Time:</span>
                          <span className="font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">{selectedApp.data.custom_peak_time}</span>
                        </p>
                      )}
                      {selectedApp.data?.peak_days && (
                        <p className="text-slate-600 text-xs">Peak Days: <strong>{selectedApp.data.peak_days}</strong></p>
                      )}
                      <p className="text-slate-600 text-xs">Approx Peak Calls/Hour: <strong>{renderVal(selectedApp.data?.approx_peak_calls_per_hour)}</strong></p>
                    </div>

                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-1.5">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">REQUIRED FLEET MIX</span>
                      {(() => {
                        const d = selectedApp.data || {};
                        const mixItems = [];

                        if (d.mix_acls_percent !== undefined && d.mix_acls_percent !== '') {
                          mixItems.push({ label: 'ACLS', val: `${d.mix_acls_percent}%`, color: 'bg-indigo-50 text-indigo-700 border-indigo-200' });
                        }
                        if (d.mix_als_percent !== undefined && d.mix_als_percent !== '') {
                          mixItems.push({ label: 'ALS', val: `${d.mix_als_percent}%`, color: 'bg-blue-50 text-blue-700 border-blue-200' });
                        }
                        if (d.mix_bls_big_percent !== undefined && d.mix_bls_big_percent !== '') {
                          mixItems.push({ label: 'BLS Big', val: `${d.mix_bls_big_percent}%`, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' });
                        }
                        if (d.mix_bls_small_percent !== undefined && d.mix_bls_small_percent !== '') {
                          mixItems.push({ label: 'BLS Small', val: `${d.mix_bls_small_percent}%`, color: 'bg-teal-50 text-teal-700 border-teal-200' });
                        } else if (!d.mix_bls_big_percent && d.mix_bls_percent !== undefined && d.mix_bls_percent !== '') {
                          mixItems.push({ label: 'BLS', val: `${d.mix_bls_percent}%`, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' });
                        }
                        if (d.mix_icu_percent !== undefined && d.mix_icu_percent !== '') {
                          mixItems.push({ label: 'ICU', val: `${d.mix_icu_percent}%`, color: 'bg-purple-50 text-purple-700 border-purple-200' });
                        }
                        if (d.mix_other_percent !== undefined && d.mix_other_percent !== '' && Number(d.mix_other_percent) > 0) {
                          mixItems.push({ label: 'Other', val: `${d.mix_other_percent}%`, color: 'bg-slate-50 text-slate-700 border-slate-200' });
                        }

                        if (d.ambulance_mix && typeof d.ambulance_mix === 'object') {
                          Object.entries(d.ambulance_mix).forEach(([k, v]) => {
                            if (v !== undefined && v !== '' && !mixItems.some(i => i.label.toLowerCase() === k.toLowerCase())) {
                              const cleanLabel = k.replace(/_/g, ' ').toUpperCase();
                              mixItems.push({ label: cleanLabel, val: `${v}%`, color: 'bg-blue-50 text-blue-700 border-blue-200' });
                            }
                          });
                        }

                        if (mixItems.length === 0) {
                          return <span className="text-slate-400 font-medium text-xs block pt-1">-</span>;
                        }

                        return (
                          <div className="flex items-center gap-1.5 font-semibold text-xs pt-1 flex-wrap">
                            {mixItems.map((item, idx) => (
                              <span key={idx} className={`px-2 py-0.5 rounded-lg border text-xs font-semibold ${item.color}`}>
                                {item.label}: {item.val}
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Unserved / Delay Reasons */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-2">
                    <span className="text-xs font-semibold text-slate-800 block">Common Reasons for Unserved / Delayed Dispatches:</span>
                    {Array.isArray(selectedApp.data?.unserved_delay_reasons) && selectedApp.data.unserved_delay_reasons.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {selectedApp.data.unserved_delay_reasons.map((r, rIdx) => (
                          <span key={rIdx} className="px-2.5 py-1 rounded-xl bg-amber-50 text-amber-800 border border-amber-200 text-xs font-medium">
                            ⚠️ {r}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="p-2 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                    )}
                    {selectedApp.data?.unserved_delay_other_reason && (
                      <p className="text-xs text-slate-600 italic bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                        Specific Delay Note: "{selectedApp.data.unserved_delay_other_reason}"
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* HOSPITAL STEP 4: AREAS & DESTINATIONS */}
              {activeTab === 'step4' && (() => {
                const areaDemandsList = ensureArray(
                  appData?.area_demands || appData?.catchment_areas || appData?.areas || selectedApp.area_demands
                ).filter(ad => ad && (ad.area || ad.area_name || ad.name || ad.pincode || ad.city || ad.calls_per_month || ad.monthly_demand));

                const destinationList = ensureArray(
                  appData?.destination_corridors || appData?.destinations || appData?.transfer_destinations || selectedApp.destination_corridors
                ).filter(d => d && (d.destination || d.destination_name || d.name || d.trips_per_month || d.monthly_trips || d.distance_km || d.distance));

                const serviceGapsList = ensureArray(appData?.service_gaps || selectedApp.service_gaps);
                const serviceGapDetails = appData?.service_gap_details || selectedApp.service_gap_details;

                return (
                  <div className="space-y-4">
                    {/* Catchment Areas Table */}
                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-800 block">
                          Primary Inbound Demand Catchment Zones:
                        </span>
                        {areaDemandsList.length > 0 && (
                          <span className="text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-full">
                            {areaDemandsList.length} Zones Defined
                          </span>
                        )}
                      </div>

                      {areaDemandsList.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs">
                            <thead>
                              <tr className="border-b border-slate-200 text-slate-500 font-semibold uppercase text-[10px]">
                                <th className="py-2.5 px-3">Catchment Area & Locality</th>
                                <th className="py-2.5 px-3">City / PIN Code</th>
                                <th className="py-2.5 px-3">Monthly Demand</th>
                                <th className="py-2.5 px-3">Inbound Share</th>
                                <th className="py-2.5 px-3">Priority Type</th>
                                <th className="py-2.5 px-3">Supply Readiness</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 font-medium">
                              {areaDemandsList.map((ad, idx) => {
                                const areaName = ad.area || ad.area_name || ad.name || '-';
                                const pinCity = [ad.city, ad.pincode].filter(Boolean).join(' - ') || (ad.pincode || ad.city || '-');
                                const monthlyCalls = ad.calls_per_month || ad.monthly_demand || ad.calls || ad.demand;
                                const inboundPct = ad.inbound_percent ? `${ad.inbound_percent}%` : (ad.inbound ? `${ad.inbound}%` : '-');
                                const priorityType = ad.primary_type || ad.priority_type || ad.type || '-';
                                const supply = ad.current_supply || (ad.distance_km ? `${ad.distance_km} km` : (ad.notes || '-'));

                                return (
                                  <tr key={idx} className="hover:bg-slate-50">
                                    <td className="py-2.5 px-3 font-semibold text-slate-800">{areaName}</td>
                                    <td className="py-2.5 px-3 text-slate-600">{pinCity}</td>
                                    <td className="py-2.5 px-3 text-blue-700 font-semibold">
                                      {monthlyCalls ? `${monthlyCalls} Calls` : '-'}
                                    </td>
                                    <td className="py-2.5 px-3 text-slate-700">{inboundPct}</td>
                                    <td className="py-2.5 px-3">
                                      <span className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-semibold text-[10px] border border-blue-200">
                                        {priorityType}
                                      </span>
                                    </td>
                                    <td className="py-2.5 px-3 text-slate-600">{supply}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="p-3 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                      )}
                    </div>

                    {/* Frequent Destinations Table */}
                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-800 block">
                          Frequent Inter-Facility Transfer Destinations & Corridors:
                        </span>
                        {destinationList.length > 0 && (
                          <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                            {destinationList.length} Corridors
                          </span>
                        )}
                      </div>

                      {destinationList.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs">
                            <thead>
                              <tr className="border-b border-slate-200 text-slate-500 font-semibold uppercase text-[10px]">
                                <th className="py-2.5 px-3">Destination Hospital / Medical Center</th>
                                <th className="py-2.5 px-3">Distance / Location</th>
                                <th className="py-2.5 px-3">Monthly Transfers</th>
                                <th className="py-2.5 px-3">Transfer Reason / Notes</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 font-medium">
                              {destinationList.map((d, idx) => {
                                const destName = d.destination || d.destination_name || d.name || '-';
                                const dist = d.distance_km ? `${d.distance_km} km` : (d.distance ? `${d.distance} km` : (d.city || '-'));
                                const trips = d.trips_per_month || d.monthly_trips || d.trips || d.transfers;
                                const reason = d.reason || d.transfer_reason || d.notes || '-';

                                return (
                                  <tr key={idx} className="hover:bg-slate-50">
                                    <td className="py-2.5 px-3 font-semibold text-slate-800">{destName}</td>
                                    <td className="py-2.5 px-3 text-slate-700">{dist}</td>
                                    <td className="py-2.5 px-3 text-blue-700 font-semibold">
                                      {trips ? `${trips} Transfers` : '-'}
                                    </td>
                                    <td className="py-2.5 px-3 text-slate-600">{reason}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="p-3 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                      )}
                    </div>

                    {/* Operational Service Gaps */}
                    <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-2.5">
                      <span className="text-xs font-semibold text-slate-800 block">
                        Operational Service Gaps & Demand Deficits:
                      </span>
                      {serviceGapsList.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {serviceGapsList.map((gap, gIdx) => (
                            <span
                              key={gIdx}
                              className="px-2.5 py-1 rounded-xl bg-amber-50 text-amber-800 border border-amber-200 text-xs font-semibold flex items-center gap-1.5"
                            >
                              <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
                              <span>{gap}</span>
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="p-2 text-center text-slate-400 font-medium bg-slate-50 rounded-xl">-</div>
                      )}
                      {serviceGapDetails && (
                        <p className="text-xs text-slate-600 italic bg-slate-50 p-3 rounded-xl border border-slate-200">
                          Operational Notes: "{serviceGapDetails}"
                        </p>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* HOSPITAL STEP 5: REVIEW & SUBMISSION */}
              {activeTab === 'step5' && (
                <div className="space-y-4">
                  {/* Submission Details */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-slate-50/70 space-y-2">
                    <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block">
                      APPLICATION SUBMISSION ATTESTATION
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Application Status</span>
                        <p className="font-semibold text-slate-800 mt-0.5">{selectedApp.status}</p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Submission Date</span>
                        <p className="font-semibold text-slate-800 mt-0.5">
                          {selectedApp.submitted_at ? new Date(selectedApp.submitted_at).toLocaleString() : '-'}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Accuracy Declaration</span>
                        <p className="font-semibold text-emerald-700 mt-0.5">
                          {selectedApp.data?.confirmed_accurate || selectedApp.confirmed_accurate ? 'Confirmed Accurate & Authorized' : '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Authorized Signatory / Administrator */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                    <span className="text-xs font-semibold text-slate-800 block">Authorized Hospital Administrator:</span>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Administrator Name</span>
                        <p className="font-semibold text-slate-800 mt-0.5">{renderVal(selectedApp.data?.admin_name || selectedApp.admin_name)}</p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Official Email</span>
                        <p className="font-semibold text-slate-800 mt-0.5">{renderVal(selectedApp.data?.admin_email || selectedApp.email || selectedApp.data?.email)}</p>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold">Contact Phone</span>
                        <p className="font-semibold text-slate-800 mt-0.5">{renderVal(selectedApp.data?.admin_phone || selectedApp.data?.emergency_phone || selectedApp.data?.mobile)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Hospital Emergency Operations Snapshot */}
                  <div className="p-4 rounded-2xl border border-slate-200/80 bg-white space-y-3">
                    <span className="text-xs font-semibold text-slate-800 block">Hospital Capacity & Emergency Infrastructure:</span>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-3 rounded-xl bg-blue-50/60 border border-blue-100">
                        <span className="text-[10px] text-blue-700 uppercase font-semibold block">Total Inpatient Beds</span>
                        <span className="text-lg font-bold text-blue-900 mt-0.5 block">{renderVal(selectedApp.data?.inpatient_beds)}</span>
                        <span className="text-[10px] text-blue-600">Operational beds</span>
                      </div>
                      <div className="p-3 rounded-xl bg-red-50/60 border border-red-100">
                        <span className="text-[10px] text-red-700 uppercase font-semibold block">ICU / Critical Care</span>
                        <span className="text-lg font-bold text-red-900 mt-0.5 block">{renderVal(selectedApp.data?.icu_beds)}</span>
                        <span className="text-[10px] text-red-600">ICU beds active</span>
                      </div>
                      <div className="p-3 rounded-xl bg-amber-50/60 border border-amber-100">
                        <span className="text-[10px] text-amber-700 uppercase font-semibold block">Trauma Center Level</span>
                        <span className="text-sm font-bold text-amber-950 mt-1 block truncate">{renderVal(selectedApp.data?.trauma_center_level)}</span>
                        <span className="text-[10px] text-amber-600">Accredited facility</span>
                      </div>
                      <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80">
                        <span className="text-[10px] text-slate-500 uppercase font-semibold block">Catchment Radius</span>
                        <span className="text-sm font-bold text-slate-800 mt-1 block">
                          {selectedApp.data?.catchment_radius_km ? `${selectedApp.data.catchment_radius_km} km` : '-'}
                        </span>
                        <span className="text-[10px] text-slate-400">Emergency reach</span>
                      </div>
                    </div>
                  </div>

                  {/* Institutional Attestation Stamp */}
                  <div className="p-4 rounded-2xl border border-emerald-200/80 bg-emerald-50/50 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-semibold text-emerald-950">Institutional Accuracy & Authorization Attested</p>
                      <p className="text-[11px] text-emerald-800/80 mt-0.5 leading-relaxed">
                        The hospital administrator has formally certified that all institutional information, facility capacities, contact details, and emergency response requirements submitted in this application are authentic, accurate, and authorized on behalf of the healthcare institution.
                      </p>
                    </div>
                  </div>

                  {/* Proceed to Phone Verification & Activation Form Banner */}
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-2xs">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0">
                        <PhoneCall className="w-5 h-5 animate-pulse" />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-slate-800 block">
                          Next: Phone Verification & Partner Activation
                        </span>
                        <p className="text-[11px] text-slate-600">
                          Review is complete. Call the hospital contact to verify registration details, select fleet operator, configure credentials and activate platform access.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setActiveTab('step6')}
                      className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs shadow-xs transition-colors shrink-0 cursor-pointer"
                    >
                      <span>Open Activation Form</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* HOSPITAL STEP 6: ADMIN PHONE VERIFICATION & ACTIVATION FORM (Matching User Image 2) */}
              {activeTab === 'step6' && (
                <div className="space-y-4 animate-fade-in">
                  {/* Call Desk Header Banner */}
                  <div className="p-4 rounded-2xl bg-gradient-to-r from-blue-50 via-sky-50 to-indigo-50 border border-blue-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-2xs">
                        <PhoneCall className="w-5 h-5 animate-pulse" />
                      </div>
                      <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700 block">
                          Super Admin Phone Verification Desk
                        </span>
                        <p className="text-xs text-slate-800 font-semibold">
                          Call hospital at <span className="font-mono text-blue-700">{activationForm.admin_phone || selectedApp.data?.admin_phone || selectedApp.data?.mobile || 'Phone not set'}</span> to verify details, link fleet operator & setup admin account.
                        </p>
                      </div>
                    </div>
                    {(activationForm.admin_phone || selectedApp.data?.admin_phone) && (
                      <a
                        href={`tel:${activationForm.admin_phone || selectedApp.data?.admin_phone}`}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-blue-200 text-blue-700 text-xs font-bold hover:bg-blue-100/50 shadow-2xs transition-colors shrink-0"
                      >
                        <Phone className="w-3.5 h-3.5 text-blue-600" />
                        <span>Call Hospital</span>
                      </a>
                    )}
                  </div>

                  {/* Header Title as per Image 2 */}
                  <div className="pb-1">
                    <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest block mb-1">
                      PARTNER REGISTRATION (SUPER ADMIN VERIFICATION)
                    </span>
                    <h2 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">
                      Become a RISA partner
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Confirm and complete details about this hospital before approving live platform access.
                    </p>
                  </div>

                  {/* Card 1: HOSPITAL INFORMATION */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <Building2 className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        HOSPITAL INFORMATION
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Hospital Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Enter hospital name"
                          value={activationForm.name || ''}
                          onChange={(e) => {
                            setActivationForm((p) => ({ ...p, name: e.target.value }));
                            if (formErrors.name) setFormErrors((p) => ({ ...p, name: '' }));
                          }}
                          className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                            formErrors.name ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {formErrors.name && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.name}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          License Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Hospital license number"
                          value={activationForm.license_number || ''}
                          onChange={(e) => {
                            setActivationForm((p) => ({ ...p, license_number: e.target.value }));
                            if (formErrors.license_number) setFormErrors((p) => ({ ...p, license_number: '' }));
                          }}
                          className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                            formErrors.license_number ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {formErrors.license_number && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.license_number}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Enter complete address"
                        value={activationForm.address || ''}
                        onChange={(e) => {
                          setActivationForm((p) => ({ ...p, address: e.target.value }));
                          if (formErrors.address) setFormErrors((p) => ({ ...p, address: '' }));
                        }}
                        className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                          formErrors.address ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                        }`}
                      />
                      {formErrors.address && (
                        <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.address}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          City <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="City"
                          value={activationForm.city || ''}
                          onChange={(e) => {
                            setActivationForm((p) => ({ ...p, city: e.target.value }));
                            if (formErrors.city) setFormErrors((p) => ({ ...p, city: '' }));
                          }}
                          className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                            formErrors.city ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {formErrors.city && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.city}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          State <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="State"
                          value={activationForm.state || ''}
                          onChange={(e) => {
                            setActivationForm((p) => ({ ...p, state: e.target.value }));
                            if (formErrors.state) setFormErrors((p) => ({ ...p, state: '' }));
                          }}
                          className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                            formErrors.state ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {formErrors.state && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.state}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Pincode <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          maxLength={6}
                          placeholder="Pincode"
                          value={activationForm.pincode || ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            setActivationForm((p) => ({ ...p, pincode: val }));
                            if (formErrors.pincode) setFormErrors((p) => ({ ...p, pincode: '' }));
                          }}
                          className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                            formErrors.pincode ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {formErrors.pincode && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.pincode}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Fleet Operator (Linked Operator)
                      </label>
                      <select
                        value={activationForm.operator_id || ''}
                        onChange={(e) => setActivationForm((p) => ({ ...p, operator_id: e.target.value }))}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all cursor-pointer"
                      >
                        <option value="">Select Fleet Operator (or Independent)</option>
                        <option value="INDEPENDENT">Independent / Self-Managed Hospital Fleet</option>
                        {fleetOperators.map((op) => (
                          <option key={op.operator_id} value={op.operator_id}>
                            {op.name} {op.city ? `(${op.city})` : ''}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] text-slate-400 mt-1">
                        Select an existing active fleet provider to serve this hospital, or leave as Independent.
                      </p>
                    </div>
                  </div>

                  {/* Card 2: ADMIN USER INFORMATION */}
                  <div className="bg-white rounded-2xl border border-slate-200/90 p-5 shadow-2xs space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                      <div className="w-7 h-7 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <User className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        ADMIN USER INFORMATION
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Admin Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="Enter admin name"
                          value={activationForm.admin_name || ''}
                          onChange={(e) => {
                            setActivationForm((p) => ({ ...p, admin_name: e.target.value }));
                            if (formErrors.admin_name) setFormErrors((p) => ({ ...p, admin_name: '' }));
                          }}
                          className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                            formErrors.admin_name ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {formErrors.admin_name && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.admin_name}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Admin Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          placeholder="Enter official hospital admin email"
                          value={activationForm.admin_email || ''}
                          onChange={(e) => {
                            setActivationForm((p) => ({ ...p, admin_email: e.target.value }));
                            if (formErrors.admin_email) setFormErrors((p) => ({ ...p, admin_email: '' }));
                          }}
                          className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all ${
                            formErrors.admin_email ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        {activationForm.admin_email?.includes('command@') && (
                          <p className="text-[11px] text-blue-700 bg-blue-50/70 border border-blue-200/80 rounded-lg p-2 mt-1.5 leading-relaxed">
                            💡 <strong>Tip:</strong> <code>{activationForm.admin_email}</code> is your Command Center account. Enter the hospital&apos;s own email (e.g. <code>hospital@example.com</code>) so the partner team can log in directly.
                          </p>
                        )}
                        {formErrors.admin_email && (
                          <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.admin_email}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">
                        Admin Phone <span className="text-red-500">*</span>
                      </label>
                      <div className="flex rounded-xl border border-slate-200 overflow-hidden focus-within:ring-1 focus-within:ring-blue-600 focus-within:border-blue-600">
                        <span className="px-3 py-2.5 bg-slate-50 border-r border-slate-200 text-xs font-semibold text-slate-600 select-none">
                          IN +91
                        </span>
                        <input
                          type="tel"
                          maxLength={10}
                          placeholder="Enter 10-digit mobile number"
                          value={activationForm.admin_phone || ''}
                          onChange={(e) => {
                            const clean = e.target.value.replace(/\D/g, '').slice(0, 10);
                            setActivationForm((p) => ({ ...p, admin_phone: clean }));
                            if (formErrors.admin_phone) setFormErrors((p) => ({ ...p, admin_phone: '' }));
                          }}
                          className="flex-1 px-3.5 py-2.5 bg-white text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none"
                        />
                      </div>
                      {formErrors.admin_phone && (
                        <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.admin_phone}</p>
                      )}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-medium text-slate-600">
                          Temporary Password <span className="text-red-500">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={handleGeneratePassword}
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-blue-600 hover:text-blue-700 cursor-pointer"
                        >
                          <Sparkles className="w-3 h-3 text-blue-600" />
                          <span>Generate</span>
                        </button>
                      </div>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Set temporary password"
                          value={activationForm.admin_password || ''}
                          onChange={(e) => {
                            setActivationForm((p) => ({ ...p, admin_password: e.target.value }));
                            if (formErrors.admin_password) setFormErrors((p) => ({ ...p, admin_password: '' }));
                          }}
                          className={`w-full pr-10 px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all font-mono ${
                            formErrors.admin_password ? 'border-red-400 bg-red-50/20' : 'border-slate-200'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((p) => !p)}
                          className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {formErrors.admin_password && (
                        <p className="text-[11px] text-red-600 mt-1 font-medium">{formErrors.admin_password}</p>
                      )}
                    </div>

                    <div className="p-3 rounded-xl bg-amber-50/80 border border-amber-200 text-amber-800 text-[11px] flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                        <span>This is a temporary password. The user will be required to change it on first login.</span>
                      </div>
                      {passwordCopied && (
                        <span className="text-emerald-700 font-bold text-[10px] bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                          Password Generated!
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Submit / Activate Button Banner */}
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleSaveAndApprove}
                      disabled={actionLoading || selectedApp.status !== 'SUBMITTED'}
                      className="w-full py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs sm:text-sm shadow-md transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 stroke-[2.5]" />}
                      <span>Approve & Activate Hospital Partner</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* INLINE DECISION ACTION PANEL */}
        {decisionAction && (
          <div
            className={`mt-4 p-4 rounded-2xl border shrink-0 animate-fade-in ${
              decisionAction === 'reject'
                ? 'bg-red-50/80 border-red-200'
                : 'bg-purple-50/80 border-purple-200'
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`w-4 h-4 ${decisionAction === 'reject' ? 'text-red-600' : 'text-purple-600'}`} />
                <h4 className="font-semibold text-slate-800 text-xs sm:text-sm">
                  {decisionAction === 'reject' ? 'Confirm Application Rejection' : 'Request Application Changes from Partner'}
                </h4>
              </div>
              <button
                type="button"
                onClick={() => {
                  setDecisionAction(null);
                  setReviewNotes('');
                  setDecisionError('');
                }}
                className="text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-[11px] text-slate-500 mb-2">
              {decisionAction === 'reject'
                ? 'Please provide a clear reason for rejection. This reason will be archived in the audit log.'
                : 'Specify what updates or documents the partner needs to correct before resubmitting.'}
            </p>

            <div className="flex flex-wrap gap-1.5 mb-2">
              {(decisionAction === 'reject' ? rejectionPresets : changeRequestPresets).map((preset, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setReviewNotes(preset)}
                  className="text-[10px] px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-slate-700 hover:border-blue-400 hover:text-blue-700 font-medium transition-colors cursor-pointer"
                >
                  + {preset.slice(0, 40)}...
                </button>
              ))}
            </div>

            <textarea
              rows={3}
              placeholder="Type clear notes / required changes for partner..."
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              className="w-full p-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 mb-2 font-sans"
            />

            {decisionError && <p className="text-xs text-red-600 mb-2 font-medium">{decisionError}</p>}

            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setDecisionAction(null);
                  setReviewNotes('');
                  setDecisionError('');
                }}
                className="px-3.5 py-1.5 rounded-xl text-slate-600 hover:bg-slate-200/60 text-xs font-semibold cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDecisionSubmit}
                disabled={actionLoading}
                className={`px-4 py-1.5 rounded-xl text-white text-xs font-semibold shadow-xs cursor-pointer flex items-center gap-1.5 ${
                  decisionAction === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-purple-600 hover:bg-purple-700'
                }`}
              >
                {actionLoading && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                <span>{decisionAction === 'reject' ? 'Submit Rejection' : 'Send Change Request'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Modal Bottom Actions Footer */}
        {!decisionAction && (
          <div className="border-t border-slate-100 pt-4 mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 font-semibold text-xs cursor-pointer transition-colors"
            >
              Close
            </button>

            <div className="flex flex-col sm:flex-row items-center gap-2.5 w-full sm:w-auto">
              <button
                type="button"
                onClick={() => {
                  setDecisionAction('reject');
                  setReviewNotes('');
                  setDecisionError('');
                }}
                disabled={actionLoading || selectedApp.status === 'APPROVED'}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl border border-red-200 text-red-600 bg-white hover:bg-red-50 font-semibold text-xs transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                <XCircle className="w-4 h-4 text-red-500" />
                <span>Reject Application</span>
              </button>

              <button
                type="button"
                onClick={handleSaveAndApprove}
                disabled={actionLoading || selectedApp.status !== 'SUBMITTED'}
                className="w-full sm:w-auto px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs shadow-xs transition-colors cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                <span>Approve & Activate Partner</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingApplicationDetailModal;
